"use client";

import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (location.pathname === "/login") {
    return <div />;
  }

  const navItems = [
    {
      name: "About",
      path: "/about",
    },
    {
      name: "Training & Certifications",
      dropdown: [
        { name: "Authorised Offices", path: "/authorised-offices" },
        { name: "All Certifications", path: "/courses" },
      ],
    },
    {
      name: "Ship Management",
      dropdown: [
        { name: "Crew Management", path: "/crew-management" },
        { name: "Technical Services", path: "/technical-services" },
        { name: "Ship Registry & Survey", path: "/registry-survey" },
        { name: "Ship Insurance", path: "/ship-insurance" },
      ],
    },
    { name: "Ship Chartering", path: "/ship-chartering" },
    { name: "Ship Brokering", path: "/ship-brokering" },
    { name: "Our Fleet", path: "/our-fleet" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 shadow-lg backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="h-12 w-[140px]">
              <img
                src={`${
                  (location.pathname === "/" ||
                    location.pathname.includes("course")) &&
                  !scrolled
                    ? "/w-logo.svg"
                    : "/logo-cropped.svg"
                }`}
                alt="Nautilus Times Logo"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden items-center space-x-1 md:flex lg:space-x-2">
            {navItems.map((item) => (
              <div key={item.name} className="group relative">
                {item.dropdown ? (
                  <div className="relative">
                    <button
                      className={`flex items-center space-x-1 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                        !(
                          location.pathname === "/" ||
                          location.pathname.includes("course")
                        ) || scrolled
                          ? "text-slate-700 hover:text-teal-600"
                          : "text-gray-300 hover:text-white"
                      }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown size={16} />
                    </button>
                    {/* Dropdown Menu */}
                    <div className="invisible absolute left-0 top-full pt-0 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                      <div className="mt-2 min-w-56 rounded-lg bg-white shadow-2xl">
                        {item.dropdown.map((dropItem) => (
                          <Link
                            key={dropItem.path}
                            to={dropItem.path}
                            className="block px-4 py-3 text-sm font-medium text-slate-700 transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg hover:bg-teal-50 hover:text-teal-600"
                          >
                            {dropItem.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`relative inline-block px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                      scrolled
                        ? location.pathname === item.path
                          ? "text-teal-600"
                          : "text-slate-700 hover:text-teal-600"
                        : location.pathname === "/" ||
                          location.pathname.includes("course")
                        ? "text-gray-300 hover:text-white"
                        : "text-slate-700 hover:text-teal-600"
                    }`}
                  >
                    {item.name}
                    {location.pathname === item.path && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600"
                      />
                    )}
                  </Link>
                )}
              </div>
            ))}
            <Link
              to="/payment"
              className="rounded-full bg-teal-600 px-6 py-2 text-white transition-colors duration-200 hover:bg-teal-700"
            >
              Enroll Now
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="rounded-md p-2 text-slate-700 hover:text-teal-600 md:hidden"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          initial={false}
          animate={{ height: isOpen ? "auto" : 0 }}
          className="mt-2 overflow-hidden rounded-lg bg-white/95 backdrop-blur-md md:hidden"
        >
          <div className="space-y-2 px-4 py-4">
            {navItems.map((item) => (
              <div key={item.name}>
                {item.dropdown ? (
                  <div>
                    <button
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === item?.name ? null : item.name
                        )
                      }
                      className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-teal-50 hover:text-teal-600"
                    >
                      <span>{item.name}</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${
                          openDropdown === item.name ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {openDropdown === item.name && (
                      <div className="ml-4 space-y-1 border-l-2 border-teal-200">
                        {item.dropdown.map((dropItem) => (
                          <Link
                            key={dropItem.path}
                            to={dropItem.path}
                            onClick={() => setIsOpen(false)}
                            className="block rounded-md px-3 py-2 text-xs font-medium text-slate-600 hover:bg-teal-50 hover:text-teal-600"
                          >
                            {dropItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                      location.pathname === item.path
                        ? "bg-teal-50 text-teal-600"
                        : "text-slate-700 hover:bg-teal-50 hover:text-teal-600"
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            <Link
              to="/payment"
              onClick={() => setIsOpen(false)}
              className="block rounded-full bg-teal-600 px-6 py-2 text-center text-white transition-colors duration-200 hover:bg-teal-700"
            >
              Enroll Now
            </Link>
          </div>
        </motion.div>
      </div>
    </motion.nav>
  );
};

export default Navbar;
